import dayjs from "dayjs";
import { useMemo } from "react";

import {
  AdoptionTimeseriesData,
  FeatureAdoptionData,
  FeatureAdoptionQuery,
  Granularity,
} from "@reflag/shared/featureAPI";
import { FeedbackList, FeedbackQuery } from "@reflag/shared/feedbackAPI";
import { SatisfactionScore } from "@reflag/shared/schemas/satisfactionScore";

function getTimeseries(
  granularity: Granularity,
  count: number,
): AdoptionTimeseriesData[] {
  return Array.from({ length: count }, (_, i) => ({
    epoch: dayjs().subtract(i, granularity).unix(),
    value: Math.floor(Math.random() * 100),
  }));
}

export function useFeatureAdoptionPlaceholderData(
  params: Omit<FeatureAdoptionQuery, "envId">,
): FeatureAdoptionData {
  const count = useMemo(() => {
    return dayjs(params.endTimestamp).diff(
      dayjs(params.startTimestamp),
      params.granularity,
    );
  }, [params.endTimestamp, params.startTimestamp, params.granularity]);

  return useMemo(
    () => ({
      exposed: {
        companies: Math.floor(Math.random() * 10000),
        users: Math.floor(Math.random() * 30000),
        timeseries: getTimeseries(params.granularity, count),
      },
      used: {
        companies: Math.floor(Math.random() * 1000),
        users: Math.floor(Math.random() * 3000),
        timeseries: getTimeseries(params.granularity, count),
      },
    }),
    [params.granularity, count],
  );
}

function randomWord(letters: number) {
  return Array.from({ length: letters }, () =>
    String.fromCharCode(Math.floor(Math.random() * 26) + 97),
  ).join("");
}

function randomSentence(words: number) {
  return Array.from({ length: words }, () =>
    randomWord(Math.floor(Math.random() * 5) + 3),
  ).join(" ");
}

export function useFeatureFeedbackPlaceholderData(
  params: Omit<FeedbackQuery, "envId">,
): FeedbackList {
  return useMemo(() => {
    return {
      data: Array.from({ length: params.pageSize ?? 5 }, (_, i) => ({
        id: `feedback-${i}`,
        companyId: `c_${i}`,
        companyName: randomSentence(1),
        companyAvatarUrl: null,
        companyFunnelStep: null,
        featureId: params.featureId!,
        featureName: null,
        featureIcon: null,
        featureIconColor: null,
        featureLinearTrackingEntity: null,
        userId: `u_${i}`,
        userName: randomSentence(2),
        userEmail: `user-${i}@example.com`,
        userAvatarUrl: null,
        question: null,
        score: (Math.floor(Math.random() * 6) + 1) as SatisfactionScore,
        comment: randomSentence(Math.floor(Math.random() * 10) + 1),
        source: "api",
        timestamp: dayjs().subtract(i, "day").toISOString(),
        updatedAt: dayjs().subtract(i, "day").toISOString(),
      })),
      metadata: {},
      pageIndex: 0,
      pageSize: params.pageSize ?? 5,
      totalCount: params.pageSize ?? 5,
      sortBy: "timestamp",
      sortOrder: "desc",
    };
  }, [params.pageSize, params.featureId]);
}
